// Occurrences of a daily trip ("pianificato").
//
// A daily trip is a SERIES: `giorni` give the weekly cadence, `start_date` /
// `end_date` the period (see `dailyTripValidity`). An OCCURRENCE is one date
// of that series. One date can differ from the series: that per-date change is
// an EXCEPTION ("eccezione" in the UI, `override` in code and data), stored in
// `daily_trips/{id}/overrides/{YYYY-MM-DD}` and mirrored on the parent document
// as the `_overrides.{YYYY-MM-DD}` cache that order generation and the
// timeline read.
//
// Twin of the backend `functions/utils/dailyTripOccurrences.js`: keep
// `OVERRIDE_FIELD_SPEC` aligned on both sides (same fixtures in both test
// suites).
import { toMomentStrict } from '@modules/utils/dates';

import {
  isWithinValidity,
  normalizeWeekdays,
  occursOn,
  referenceDay,
  VALIDITY_DATE_FORMAT,
} from './dailyTripValidity';

/**
 * What an exception (override) may change. `nullMeans`:
 * - `inherit`: null/absent falls back to the series value (a blank field in
 *   the occurrence form means "as usual");
 * - `clear`: an explicit null wins — "no driver on this date", "no group on
 *   this date" are real answers.
 */
export const OVERRIDE_FIELD_SPEC = {
  // Schedule & price
  orario_presa: { nullMeans: 'inherit' },
  // Planned arrival at the drop-off (from the import, and from the group
  // schedule): on a return it is the time that moves.
  orario_arrivo: { nullMeans: 'inherit' },
  prezzo: { nullMeans: 'inherit' },
  tempo: { nullMeans: 'inherit' },
  distanza_num: { nullMeans: 'inherit' },
  note: { nullMeans: 'inherit' },
  // Route
  partenza: { nullMeans: 'inherit' },
  indirizzo_da: { nullMeans: 'inherit' },
  latlng_da: { nullMeans: 'inherit' },
  architectural_barriers_from: { nullMeans: 'inherit' },
  arrivo: { nullMeans: 'inherit' },
  indirizzo_a: { nullMeans: 'inherit' },
  latlng_a: { nullMeans: 'inherit' },
  architectural_barriers_to: { nullMeans: 'inherit' },
  // Assignment
  autista: { nullMeans: 'clear' },
  id_autista: { nullMeans: 'clear' },
  nome_autista: { nullMeans: 'clear' },
  secondo_operatore: { nullMeans: 'clear' },
  secondo_operatore_arrivo: { nullMeans: 'clear' },
  id_gestore: { nullMeans: 'inherit' },
  // Passengers & service
  carrozzina: { nullMeans: 'inherit' },
  no_carrozzina: { nullMeans: 'inherit' },
  allettati: { nullMeans: 'inherit' },
  accompagnatori: { nullMeans: 'inherit' },
  accompagnatore_richiesto: { nullMeans: 'inherit' },
  require_wheelchair: { nullMeans: 'inherit' },
  autonomo: { nullMeans: 'inherit' },
  trip_type: { nullMeans: 'inherit' },
  item_type: { nullMeans: 'inherit' },
  fermo_auto: { nullMeans: 'inherit' },
  fermo_auto_incluso: { nullMeans: 'inherit' },
  // Pooling: the trip rides in another group — or alone — on this date.
  group_id: { nullMeans: 'clear' },
};

export const OVERRIDABLE_FIELDS = new Set(Object.keys(OVERRIDE_FIELD_SPEC));

/** Metadata stored next to the fields of an override entry. */
const OVERRIDE_META_KEYS = new Set(['date', '_created_at', '_updated_at', 'id']);

/**
 * Keys that never count as a change of the trip: server-owned bookkeeping,
 * values derived from another field (`giorno` from `giorni`), and form-only
 * defaults. Everything that is neither ignored nor overridable is
 * permanent-only (weekdays, period, company, category, passenger profile,
 * payment…) — the safe default for a field nobody classified.
 */
export const IGNORED_DIFF_FIELDS = new Set([
  'id',
  '_overrides',
  '_override_group_ids',
  'revisions',
  'history',
  'insertions',
  'last_insertion',
  'data_inserimento',
  'keyMapping',
  'key_mapping',
  'updated_at',
  'updated_by',
  'giorno',
  'richiedente',
]);

const fmt = (day) => day.format(VALIDITY_DATE_FORMAT);
const toDay = (value) => toMomentStrict(value);

/** Date key (`YYYY-MM-DD`) of any date-ish value, or null. */
export function occurrenceKey(date) {
  const day = toDay(date);
  return day ? fmt(day) : null;
}

/** Weekday cadence + validity, ignoring the manual active/deleted state. */
const occursOnIgnoringState = (trip, day) =>
  normalizeWeekdays(trip?.giorni, trip?.giorno).includes(day.day()) && isWithinValidity(trip, day);

/**
 * First occurrence on or after `fromDay` (default: today).
 *
 * Weekly cadence means one scan of 7 days from the start of the window is
 * enough: if nothing matches there, nothing ever will (or the period ended).
 *
 * `requireInService: false` ignores `attivo`/`deleted` — pricing a paused trip
 * still needs a real date to price.
 *
 * @returns {import('dayjs').Dayjs|null}
 */
export function nextOccurrence(trip, fromDay, { requireInService = true } = {}) {
  if (!trip) return null;
  let cursor = referenceDay(fromDay);
  const start = toDay(trip.start_date);
  if (start && cursor.isBefore(start, 'day')) cursor = start.startOf('day');
  const end = toDay(trip.end_date);
  const matches = requireInService ? occursOn : occursOnIgnoringState;

  for (let i = 0; i < 7; i += 1) {
    const day = cursor.add(i, 'day');
    if (end && day.isAfter(end, 'day')) return null;
    if (matches(trip, day)) return day;
  }
  return null;
}

/** Occurrence dates (`YYYY-MM-DD`, ascending) in `[from, to]`, both inclusive. */
export function occurrencesInRange(trip, from, to, { max = 366 } = {}) {
  const start = toDay(from);
  const end = toDay(to);
  if (!trip || !start || !end || end.isBefore(start, 'day')) return [];
  const out = [];
  let day = start.startOf('day');
  for (let i = 0; i <= max && !day.isAfter(end, 'day'); i += 1) {
    if (occursOn(trip, day)) out.push(fmt(day));
    day = day.add(1, 'day');
  }
  return out;
}

/** Changed fields of the exception on `date`, without metadata; or null. */
export function getOverride(trip, date) {
  const key = occurrenceKey(date);
  const entry = key ? trip?._overrides?.[key] : null;
  if (!entry || typeof entry !== 'object') return null;
  const fields = Object.fromEntries(
    Object.entries(entry).filter(([k]) => !OVERRIDE_META_KEYS.has(k))
  );
  return Object.keys(fields).length > 0 ? fields : null;
}

export const hasOverrideOn = (trip, date) => getOverride(trip, date) !== null;

/**
 * Exceptions from `fromDay` on, ascending. `orphan` marks an entry on
 * a date the trip no longer occurs on (weekdays or period changed after it was
 * saved): it has no effect, and the UI should say so.
 *
 * @returns {{ date: string, fields: object, orphan: boolean }[]}
 */
export function upcomingOverrides(trip, fromDay, { limit } = {}) {
  const from = fmt(referenceDay(fromDay));
  const out = Object.keys(trip?._overrides || {})
    .filter((date) => date >= from)
    .sort()
    .map((date) => ({ date, fields: getOverride(trip, date), orphan: !occursOn(trip, date) }))
    .filter((o) => o.fields);
  return limit ? out.slice(0, limit) : out;
}

/**
 * The trip as it runs on `date`: series values with that date's exception
 * applied (see `OVERRIDE_FIELD_SPEC.nullMeans`).
 *
 * An address overridden without coordinates drops the series coordinates —
 * they would point to the old place (the backend re-geocodes it). The address
 * aliases (`indirizzo_da`/`indirizzo_a`) follow `partenza`/`arrivo`.
 *
 * `_override` is `{ date, fields }` when the date has an exception, else null.
 */
export function applyOverride(trip, date) {
  const key = occurrenceKey(date);
  const override = trip && key ? getOverride(trip, key) : null;
  if (!override) return { ...trip, _override: null };

  const next = { ...trip };
  const applied = [];
  Object.entries(override).forEach(([field, value]) => {
    const spec = OVERRIDE_FIELD_SPEC[field];
    if (!spec) return;
    const isBlank = value === null || value === undefined || value === '';
    if (isBlank && spec.nullMeans !== 'clear') return;
    next[field] = isBlank ? null : value;
    applied.push(field);
  });

  if ('partenza' in override && override.partenza) {
    if (!('indirizzo_da' in override)) next.indirizzo_da = override.partenza;
    if (!('latlng_da' in override)) next.latlng_da = null;
  }
  if ('arrivo' in override && override.arrivo) {
    if (!('indirizzo_a' in override)) next.indirizzo_a = override.arrivo;
    if (!('latlng_a' in override)) next.latlng_a = null;
  }

  next._override = { date: key, fields: applied.sort() };
  return next;
}

/** Pooling group the trip rides in on `date` (the exception wins). */
export const effectiveGroupId = (trip, date) => applyOverride(trip, date).group_id ?? null;

/** Fields an exception decides together: carrying one covers the others. */
const FIELD_FAMILIES = [
  ['partenza', 'indirizzo_da', 'latlng_da'],
  ['arrivo', 'indirizzo_a', 'latlng_a'],
  ['autista', 'id_autista', 'nome_autista'],
];

/**
 * The part of a permanent change that reaches `date`: `fields` without the
 * ones that date's exception carries. An exception wins field by field — a
 * note changed for one day does not freeze that day's pickup time.
 */
export function fieldsNotOverridden(trip, date, fields = {}) {
  const override = getOverride(trip, date);
  if (!override) return { ...fields };

  const carried = new Set();
  Object.entries(override).forEach(([field, value]) => {
    const spec = OVERRIDE_FIELD_SPEC[field];
    if (!spec) return;
    const isBlank = value === null || value === undefined || value === '';
    if (isBlank && spec.nullMeans !== 'clear') return;
    (FIELD_FAMILIES.find((family) => family.includes(field)) || [field]).forEach((k) =>
      carried.add(k)
    );
  });
  return Object.fromEntries(Object.entries(fields).filter(([k]) => !carried.has(k)));
}

// --- Change classification ---------------------------------------------------

const DATE_FIELDS = new Set(['start_date', 'end_date']);
const LATLNG_FIELDS = new Set(['latlng_da', 'latlng_a']);

const round6 = (n) => Math.round(Number(n) * 1e6) / 1e6;

/** Comparable form of a value, so equal data in different shapes is not a change. */
function comparable(field, value) {
  if (value === undefined || value === '') return null;
  if (value === null) return null;
  if (DATE_FIELDS.has(field)) return occurrenceKey(value);
  if (field === 'giorni') return normalizeWeekdays(value).join(',');
  if (LATLNG_FIELDS.has(field)) {
    const lat = value?.lat ?? value?.latitude ?? value?._latitude;
    const lng = value?.lng ?? value?.longitude ?? value?._longitude;
    return lat == null || lng == null ? null : `${round6(lat)},${round6(lng)}`;
  }
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
}

/**
 * What changed between two versions of a trip, split by what an exception
 * can carry.
 *
 * @returns {{
 *   changes: { field: string, before: any, after: any, overridable: boolean }[],
 *   overridable: object,   // { field: newValue } — can go to an exception
 *   permanentOnly: object, // { field: newValue } — only a permanent change
 * }}
 */
export function classifyChanges(before = {}, after = {}) {
  const fields = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const changes = [];
  const overridable = {};
  const permanentOnly = {};

  [...fields].sort().forEach((field) => {
    if (IGNORED_DIFF_FIELDS.has(field) || field.startsWith('_')) return;
    const a = comparable(field, before?.[field]);
    const b = comparable(field, after?.[field]);
    if (a === b) return;
    const isOverridable = OVERRIDABLE_FIELDS.has(field);
    const value = after?.[field] === '' || after?.[field] === undefined ? null : after[field];
    changes.push({
      field,
      before: before?.[field] ?? null,
      after: value,
      overridable: isOverridable,
    });
    (isOverridable ? overridable : permanentOnly)[field] = value;
  });

  return { changes, overridable, permanentOnly };
}
